import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail, eur } from "@/lib/api";
import { PageHeader, ConfirmDelete, usePaginated, Paginator } from "@/components/common";
import InvoicePrint from "@/components/InvoicePrint";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Search, Printer, Trash2, Loader2, FileText, Eye, Mail, Download, Pencil, CheckCircle2, MailCheck } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPES = [["ostra", "Faktúra"], ["ponuka", "Cenová ponuka"], ["zalohova", "Zálohová faktúra"]];
const FILTERS = [["", "Všetky"], ...DOC_TYPES];
const SORTS = [["created_desc", "Najnovšie"], ["number_desc", "Číslo ↓"], ["number_asc", "Číslo ↑"], ["unpaid_first", "Neuhradené prvé"], ["paid_first", "Uhradené prvé"]];
const EF_INV_LABEL = { delivered: "Doručené", sent: "Odoslané", failed: "Zlyhalo", queued: "V rade" };

function overdueDays(inv) {
  if (!inv.due_date || inv.status === "paid") return 0;
  const due = new Date(inv.due_date + "T00:00:00");
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today - due) / 86400000));
}
function fmtDT(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("sk-SK", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
const invHasVat = (inv) => Number(inv.vat_total || 0) > 0;

export default function Invoices() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("created_desc");
  const [statusFilter, setStatusFilter] = useState("");
  const [preview, setPreview] = useState(null);
  const [settings, setSettings] = useState({});
  const [qr, setQr] = useState(null);
  const [sending, setSending] = useState(null);
  const [emailPrompt, setEmailPrompt] = useState({ open: false, id: null, value: "", kind: "invoice" });
  const [emailChoice, setEmailChoice] = useState({ open: false, id: null });

  const load = useCallback(async () => {
    const { data } = await api.get("/invoices", { params: { q, doc_type: filter } });
    setItems(data);
  }, [q, filter]);
  useEffect(() => { load(); }, [load]);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const pid = searchParams.get("preview");
    if (pid) { openPreview(pid).catch(() => toast.error("Faktúru sa nepodarilo načítať")); setSearchParams({}, { replace: true }); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isUnopened = (inv) => inv.last_emailed_at && !(inv.pdf_open_count > 0);
  const matchStatus = (inv) => {
    if (statusFilter === "unpaid") return inv.status !== "paid" && inv.doc_type !== "ponuka";
    if (statusFilter === "overdue") return overdueDays(inv) > 0;
    if (statusFilter === "unopened") return isUnopened(inv);
    return true;
  };
  const sorted = [...items].filter(matchStatus).sort((a, b) => {
    const paid = (x) => (x.status === "paid" ? 1 : 0);
    switch (sort) {
      case "number_desc": return (b.number || "").localeCompare(a.number || "");
      case "number_asc": return (a.number || "").localeCompare(b.number || "");
      case "unpaid_first": return (paid(a) - paid(b)) || (b.created_at || "").localeCompare(a.created_at || "");
      case "paid_first": return (paid(b) - paid(a)) || (b.created_at || "").localeCompare(a.created_at || "");
      default: return (b.created_at || "").localeCompare(a.created_at || "");
    }
  });
  const pg = usePaginated(sorted);
  const openPreview = async (id) => {
    setQr(null);
    const [inv, s] = await Promise.all([api.get(`/invoices/${id}`), api.get("/settings")]);
    setSettings(s.data); setPreview(inv.data);
    api.get(`/invoices/${id}/qr`).then((r) => setQr(r.data)).catch(() => setQr({ available: false }));
  };

  const printInvoice = (id) => printPdf(id);

  const togglePaid = async (inv) => {
    const status = inv.status === "paid" ? "sent" : "paid";
    try {
      await api.put(`/invoices/${inv.id}`, { status });
      toast.success(status === "paid" ? "Označené ako uhradené" : "Označené ako neuhradené");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/invoices/${id}`); toast.success("Zmazané"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const downloadPdf = async (id) => {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `faktura_${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("PDF sa nepodarilo vygenerovať"); }
  };

  const printPdf = async (id) => {
    try {
      const res = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed"; iframe.style.right = "0"; iframe.style.bottom = "0";
      iframe.style.width = "0"; iframe.style.height = "0"; iframe.style.border = "0";
      iframe.src = url;
      iframe.onload = () => { setTimeout(() => { iframe.contentWindow.focus(); iframe.contentWindow.print(); }, 300); };
      document.body.appendChild(iframe);
      setTimeout(() => { iframe.remove(); URL.revokeObjectURL(url); }, 60000);
    } catch (e) { toast.error("PDF sa nepodarilo vygenerovať"); }
  };

  const sendEmail = async (id, email, kind = "invoice") => {
    setSending(id);
    try {
      const { data } = await api.post(`/invoices/${id}/send-email`, { ...(email ? { email } : {}), kind });
      toast.success(`Odoslané na ${data.sent_to}`);
      setEmailPrompt({ open: false, id: null, value: "", kind: "invoice" });
      setEmailChoice({ open: false, id: null });
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 400 && detail === "NEEDS_EMAIL") setEmailPrompt({ open: true, id, value: "", kind });
      else if (e.response?.status === 400 && detail === "NOT_OVERDUE") toast.error("Faktúra nie je po splatnosti — upomienka sa neodoslala.");
      else toast.error(formatApiErrorDetail(detail) || "Email sa nepodarilo odoslať");
    }
    finally { setSending(null); }
  };

  return (
    <div>
      <PageHeader title="Faktúry" subtitle="Faktúry, ponuky a zálohové faktúry"
        action={<Button className="min-h-[48px] gap-2" onClick={() => navigate("/faktury/nova")} data-testid="new-invoice-button"><Plus className="h-5 w-5" /> Nová faktúra</Button>} />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input className="min-h-[48px] pl-9" placeholder="Hľadať číslo, zákazníka, VS…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="invoice-search" />
        </div>
        <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
          {FILTERS.map(([val, lbl]) => (
            <button key={val} onClick={() => setFilter(val)} data-testid={`invoice-filter-${val || "all"}`}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${filter === val ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{lbl}</button>
          ))}
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="h-11 w-[180px]" data-testid="invoice-sort-select"><SelectValue /></SelectTrigger>
          <SelectContent>{SORTS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
          {[["", "Všetky"], ["unpaid", "Neuhradené"], ["overdue", "Po splatnosti"], ["unopened", "Neotvorené"]].map(([val, lbl]) => (
            <button key={val} onClick={() => setStatusFilter(val)} data-testid={`invoice-sfilter-${val || "all"}`}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${statusFilter === val ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{lbl}</button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        {sorted.length ? pg.paged.map((inv) => (
          <div key={inv.id} className="card-surface flex flex-wrap items-center justify-between gap-3 p-4" data-testid={`invoice-row-${inv.id}`}>
            <div className="flex items-center gap-3">
              <div className="grid h-10 w-10 place-items-center rounded-lg bg-secondary"><FileText className="h-5 w-5 text-primary" /></div>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-heading text-lg font-bold">{inv.number}</span>
                  <span className="rounded bg-secondary px-2 py-0.5 text-[10px] font-bold uppercase">{DOC_TYPES.find((d) => d[0] === inv.doc_type)?.[1]}</span>
                  {inv.status === "paid"
                    ? <span className="rounded bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-400">Uhradená</span>
                    : inv.doc_type !== "ponuka" && overdueDays(inv) > 0 && <span className="rounded bg-red-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white" data-testid={`overdue-${inv.id}`}>{overdueDays(inv)} dní po splatnosti</span>}
                  {inv.pdf_open_count > 0
                    ? <span className="flex items-center gap-1 rounded bg-emerald-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-emerald-400" title={`Naposledy otvorené: ${fmtDT(inv.pdf_last_opened_at)}`} data-testid={`pdf-opened-${inv.id}`}><Eye className="h-3 w-3" /> Zobrazené {inv.pdf_open_count}×</span>
                    : inv.last_emailed_at && <span className="flex items-center gap-1 rounded bg-amber-500/15 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-400" title={`Odoslané: ${fmtDT(inv.last_emailed_at)}`} data-testid={`pdf-unopened-${inv.id}`}><Mail className="h-3 w-3" /> Odoslané – neotvorené</span>}
                  {inv.efaktura_status && <span className={`flex items-center gap-1 rounded px-2 py-0.5 text-[10px] font-bold uppercase ${inv.efaktura_status === "delivered" ? "bg-emerald-500/15 text-emerald-400" : inv.efaktura_status === "failed" ? "bg-red-500/15 text-red-400" : "bg-sky-500/15 text-sky-400"}`} title={inv.efaktura_sent_at ? `Peppol: ${fmtDT(inv.efaktura_sent_at)}${inv.efaktura_error ? " · " + inv.efaktura_error : ""}` : ""} data-testid={`efaktura-badge-${inv.id}`}>Peppol: {EF_INV_LABEL[inv.efaktura_status] || inv.efaktura_status}</span>}
                </div>
                <div className="text-sm text-muted-foreground">{inv.customer?.name || "—"} · {inv.issue_date}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-lg font-bold text-primary">{eur(inv.total)} <span className="text-xs font-normal text-muted-foreground">{invHasVat(inv) ? "s DPH" : "bez DPH"}</span></span>
              {inv.doc_type !== "ponuka" && (
                <label className="flex cursor-pointer items-center gap-1.5 text-xs font-semibold" title="Prepnúť stav úhrady">
                  <Switch checked={inv.status === "paid"} onCheckedChange={() => togglePaid(inv)} data-testid={`paid-switch-${inv.id}`} />
                  <span className={inv.status === "paid" ? "text-primary" : "text-muted-foreground"}>{inv.status === "paid" ? "Uhradená" : "Neuhradená"}</span>
                </label>
              )}
              <Button variant="secondary" size="sm" className="gap-1" onClick={() => openPreview(inv.id)} data-testid={`preview-invoice-${inv.id}`}><Eye className="h-3.5 w-3.5" /> Náhľad</Button>
              <Button variant="ghost" size="icon" onClick={() => printInvoice(inv.id)} title="Tlačiť" data-testid={`print-invoice-${inv.id}`}><Printer className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => navigate(`/faktury/${inv.id}/upravit`)} title="Upraviť" data-testid={`edit-invoice-${inv.id}`}><Pencil className="h-4 w-4" /></Button>
              <Button variant="ghost" size="icon" onClick={() => setEmailChoice({ open: true, id: inv.id })} disabled={sending === inv.id} title="Poslať emailom" data-testid={`email-invoice-${inv.id}`}>{sending === inv.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4 text-blue-400" />}</Button>
              <Button variant="ghost" size="icon" onClick={() => downloadPdf(inv.id)} title="Stiahnuť PDF" data-testid={`pdf-invoice-${inv.id}`}><Download className="h-4 w-4" /></Button>
              <ConfirmDelete onConfirm={() => remove(inv.id)} description={`Doklad č. ${inv.number} bude natrvalo odstránený.`}>
                <Button variant="ghost" size="icon" data-testid={`delete-invoice-${inv.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </ConfirmDelete>
            </div>
          </div>
        )) : null}
        {!sorted.length && <div className="py-12 text-center text-muted-foreground">Žiadne faktúry.</div>}
      </div>

      <Paginator {...pg} />

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-h-[95vh] max-w-4xl overflow-y-auto bg-background p-0">
          <div className="no-print flex items-center justify-between border-b border-border p-4">
            <DialogTitle className="font-heading text-xl uppercase">Náhľad dokladu</DialogTitle>
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => preview && downloadPdf(preview.id)} className="gap-2" data-testid="invoice-pdf-btn"><Download className="h-4 w-4" /> PDF</Button>
              <Button variant="secondary" onClick={() => preview && setEmailChoice({ open: true, id: preview.id })} disabled={sending === preview?.id} className="gap-2" data-testid="invoice-email-btn">{sending === preview?.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Email</Button>
              <Button onClick={() => preview && printPdf(preview.id)} className="gap-2" data-testid="invoice-print-btn"><Printer className="h-4 w-4" /> Tlač</Button>
            </div>
          </div>
          {preview && (
            <div className="no-print mx-4 mt-4 flex flex-wrap gap-4 rounded-lg border border-border bg-card/60 p-3 text-xs" data-testid="invoice-history">
              <span className="flex items-center gap-1.5">{preview.last_emailed_at ? <MailCheck className="h-4 w-4 text-blue-400" /> : <Mail className="h-4 w-4 text-muted-foreground" />}<span className="text-muted-foreground">Odoslané:</span> <b>{preview.last_emailed_at ? `${fmtDT(preview.last_emailed_at)}${preview.last_emailed_to ? " → " + preview.last_emailed_to : ""}` : "—"}</b></span>
              <span className="flex items-center gap-1.5">{preview.pdf_open_count > 0 ? <CheckCircle2 className="h-4 w-4 text-emerald-400" /> : <Eye className="h-4 w-4 text-muted-foreground" />}<span className="text-muted-foreground">Zákazník otvoril PDF:</span> <b>{preview.pdf_open_count > 0 ? `${preview.pdf_open_count}× · naposledy ${fmtDT(preview.pdf_last_opened_at)}` : "zatiaľ nie"}</b></span>
              {preview.pdf_first_opened_at && <span className="flex items-center gap-1.5"><span className="text-muted-foreground">Prvýkrát:</span> <b>{fmtDT(preview.pdf_first_opened_at)}</b></span>}
            </div>
          )}
          <div className="print-area p-4">
            <InvoicePrint invoice={preview} settings={settings} qr={qr} />
            {qr && !qr.available && <div className="no-print mx-auto mt-3 max-w-[800px] rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-300">{qr.message}</div>}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={emailChoice.open} onOpenChange={(o) => !o && setEmailChoice({ open: false, id: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Odoslať e-mail zákazníkovi</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Vyberte, aký e-mail sa má odoslať. Upomienku je možné odoslať iba pri faktúre po splatnosti.</p>
            <div className="flex flex-col gap-2">
              <Button onClick={() => sendEmail(emailChoice.id, null, "invoice")} disabled={sending === emailChoice.id} className="gap-2" data-testid="email-choice-invoice"><FileText className="h-4 w-4" /> Odoslať faktúru</Button>
              <Button variant="outline" onClick={() => sendEmail(emailChoice.id, null, "reminder")} disabled={sending === emailChoice.id} className="gap-2" data-testid="email-choice-reminder"><MailCheck className="h-4 w-4" /> Odoslať upomienku</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={emailPrompt.open} onOpenChange={(o) => !o && setEmailPrompt({ open: false, id: null, value: "", kind: "invoice" })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl uppercase">Zadajte email zákazníka</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Tento zákazník nemá uložený email. Zadajte ho – uložíme ho ku zákazníkovi a pošleme faktúru.</p>
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" placeholder="zakaznik@email.sk" value={emailPrompt.value} onChange={(e) => setEmailPrompt((p) => ({ ...p, value: e.target.value }))} data-testid="email-prompt-input" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEmailPrompt({ open: false, id: null, value: "", kind: "invoice" })}>Zrušiť</Button>
            <Button onClick={() => sendEmail(emailPrompt.id, emailPrompt.value.trim(), emailPrompt.kind)} disabled={!emailPrompt.value.trim() || sending === emailPrompt.id} data-testid="email-prompt-send">{sending === emailPrompt.id ? <Loader2 className="h-4 w-4 animate-spin" /> : "Uložiť a odoslať"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
